/**
 * advisoryTextSanitize.ts
 *
 * The single control-character normalizer every advisory free-text surface
 * calls. Extracted from the request-builder module that originally hosted
 * `normalizeSingleLine` (since deleted - see below), which pre-dates this
 * module and was imported by nothing except that module's own request
 * builders - two of the four named free-text surfaces (the intake
 * question, the goal-refinement text) reached an LLM-backed endpoint with
 * only `.trim()` applied. A shared function is what makes the guarantee
 * structural rather than a convention three call sites happened to follow.
 *
 * ASVS V5 defence in depth: every field any of the four surfaces builds a
 * request from comes out of a single-line MUI `InputBase` (never
 * `multiline`), so a *typed* Enter cannot deliver a literal newline. But
 * paste behaviour for control characters in single-line inputs is not
 * standardised across browsers and must not be relied on - this function
 * makes the guarantee structural rather than incidental, collapsing every
 * CRLF/LF/CR and any other C0 control character (plus DEL, the byte after
 * printable ASCII) to a single space, then trimming and collapsing
 * whitespace runs, so structured-request injection is impossible even from
 * a hostile paste that somehow smuggled a control character past the input
 * element.
 *
 * `normalizeControlCharacters` applies NO length bound and is what the
 * intake question and the goal-refinement text call, because those are
 * legitimately longer and a silent truncation with no UI feedback would be
 * worse than the risk this module addresses. `normalizeSingleLine` below is
 * the bounded composition - currently unconsumed, since the two request
 * builders it used to serve (`buildAddCriterionRequest`,
 * `buildAddKeyQuestionRequest`) were deleted along with their host module
 * (both sent DTO fields the supervisor silently dropped). Kept for a
 * caller that needs a single bounded short sentence - see its own docblock
 * for why it was retained rather than deleted along with its host module.
 */

/**
 * Collapses every C0 control character (from NUL through Unit Separator)
 * plus DEL in `text` to a single space, then trims and collapses
 * whitespace runs. Applies NO length bound - callers that need one use
 * `normalizeSingleLine` below, which composes this function with
 * `MAX_EDIT_TEXT_LENGTH`, never a bound applied before this one runs.
 */
export function normalizeControlCharacters(text: string): string {
  return (
    text
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001F\u007F]/g, ' ')
      .trim()
      .replace(/\s+/g, ' ')
  );
}

/**
 * The maximum length of any free text sent through `normalizeSingleLine`
 * below.
 *
 * Client-owned, and mirrors nothing server-side - it is a bound on what this
 * client will SEND, not a validation rule the supervisor enforces.
 * `normalizeSingleLine` neutralised control characters but imposed no
 * maximum, and no `maxLength` was set on any `InputBase`, so a pasted
 * megabyte of text was normalised and shipped to an LLM-backed endpoint,
 * then rendered verbatim as a row once the agent wrote it (the render path
 * has no truncation either - unlike `truncateGoalTitle`'s 80-character cap
 * for list rows).
 *
 * 500 is generous for a single sentence while still bounding the request.
 * It currently has no live consumer: the module that used to host both this
 * constant and `normalizeSingleLine` - whose two request builders were the
 * only callers of `normalizeSingleLine` - was deleted because both builders
 * sent DTO fields (`addCriterion`, `addKeyQuestion`) the supervisor's
 * `extra="ignore"` config silently dropped. This constant was retained
 * rather than deleted alongside that module because it is a
 * defence-in-depth finding orthogonal to the contract correction that
 * deleted that module - dropping it would silently regress a real
 * code-review finding. Its expected next consumer is a future
 * typed-free-text add flow that reintroduces a bounded single-line field
 * for the propose/accept add-a-question turn.
 */
export const MAX_EDIT_TEXT_LENGTH = 500;

/**
 * ASVS V5 defence in depth, length-bound half: composes
 * `normalizeControlCharacters` with `MAX_EDIT_TEXT_LENGTH`.
 *
 * The `.slice()` below runs LAST, after `normalizeControlCharacters` has
 * already trimmed and collapsed whitespace, so the bound applies to the
 * string actually sent rather than to a raw value that collapse might have
 * shortened anyway. That ordering is the reason this composition exists as
 * its own function rather than being inlined at each call site - collapse
 * first, bound last, always in that order.
 */
export function normalizeSingleLine(text: string): string {
  return normalizeControlCharacters(text).slice(0, MAX_EDIT_TEXT_LENGTH);
}
